import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Avatar,
  Box,
  Card,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';

import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import SecurityIcon from '@mui/icons-material/Security';
import GppBadIcon from '@mui/icons-material/GppBad';
import LockPersonIcon from '@mui/icons-material/LockPerson';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';

import SearchBar from '../../components/mui/SearchBar';
import CustomButton from '../../components/mui/CustomButton';
import FraudModal from '../../components/FraudModal';
import { getFraudUsers } from '../../actions/userAuth';

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.04, duration: 0.45, ease: [0.22, 1, 0.36, 1] },
  }),
};

const PanelCard = ({ children, sx = {} }) => (
  <Card
    elevation={0}
    sx={{
      borderRadius: '24px',
      border: '1px solid',
      borderColor: alpha('#11409B', 0.08),
      bgcolor: '#fff',
      boxShadow: '0 4px 32px rgba(15, 23, 42, 0.06)',
      overflow: 'hidden',
      ...sx,
    }}
  >
    {children}
  </Card>
);

const formatPhone = (phone) => {
  if (!phone) return '—';
  const cleaned = String(phone).replace(/\s/g, '');
  if (cleaned.startsWith('+')) return cleaned;
  if (/^91\d{10}$/.test(cleaned)) return `+${cleaned}`;
  return `+91 ${cleaned}`;
};

export const FraudUsersTab = () => {
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(true);
  const [fraudUsers, setFraudUsers] = useState([]);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  // Selected user for FraudModal action
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const fetchFraudUsers = useCallback(() => {
    setLoading(true);
    dispatch(
      getFraudUsers(
        {
          page,
          size,
          search: searchTerm || undefined,
        },
        (response) => {
          const body = response?.data ?? response;
          const raw =
            body?.data?.content ||
            body?.content ||
            body?.data?.users ||
            body?.users ||
            body?.data?.fraudUsers ||
            body?.fraudUsers ||
            (Array.isArray(body?.data?.data) ? body.data.data : null) ||
            (Array.isArray(body?.data) ? body.data : null) ||
            (Array.isArray(body) ? body : null) ||
            [];

          const totalElems =
            body?.data?.totalElements ??
            body?.totalElements ??
            body?.data?.totalCount ??
            body?.totalCount ??
            body?.data?.total ??
            body?.total ??
            (Array.isArray(raw) ? raw.length : 0);

          const totPages =
            body?.data?.totalPages ??
            body?.totalPages ??
            (Math.ceil(totalElems / size) || 1);

          let finalContent = Array.isArray(raw) ? raw : [];
          if (searchTerm && finalContent.length > 0) {
            const termLower = searchTerm.toLowerCase();
            const filtered = finalContent.filter((u) => {
              return (
                (u?.name && u.name.toLowerCase().includes(termLower)) ||
                (u?.username && u.username.toLowerCase().includes(termLower)) ||
                (u?.email && u.email.toLowerCase().includes(termLower)) ||
                (u?.phone && String(u.phone).includes(searchTerm)) ||
                (u?.fraudReason && u.fraudReason.toLowerCase().includes(termLower)) ||
                (u?.fraudFlaggedBy && u.fraudFlaggedBy.toLowerCase().includes(termLower)) ||
                (u?.id && String(u.id).toLowerCase().includes(termLower))
              );
            });
            if (filtered.length !== finalContent.length && filtered.length > 0) {
              finalContent = filtered;
            }
          }

          setFraudUsers(finalContent);
          setTotalElements(totalElems);
          setTotalPages(totPages);
          setLoading(false);
        },
        (err) => {
          console.error('Failed to fetch fraud / blacklisted users:', err);
          setLoading(false);
        }
      )
    );
  }, [dispatch, page, size, searchTerm]);

  useEffect(() => {
    fetchFraudUsers();
  }, [fetchFraudUsers]);

  const stats = useMemo(() => {
    let systemAutoCount = 0;
    let adminFlaggedCount = 0;

    fraudUsers.forEach((u) => {
      const by = String(u?.fraudFlaggedBy || '').toUpperCase();
      if (by === 'SYSTEM') systemAutoCount++;
      else adminFlaggedCount++;
    });

    return {
      totalFraud: totalElements,
      systemAutoCount,
      adminFlaggedCount,
      blockedCount: totalElements,
    };
  }, [fraudUsers, totalElements]);

  const handleSearch = (term) => {
    setSearchTerm(term);
    setPage(0);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setPage(0);
  };

  const handleOpenActionModal = (user) => {
    setSelectedUser(user);
    setModalOpen(true);
  };

  const exportToExcel = () => {
    const header = [
      'User ID',
      'Name',
      'Email',
      'Phone',
      'Username',
      'Blacklist Reason',
      'Flagged By',
      'Flagged At',
      'Blocked',
      'Active',
    ];

    const dataToExport = fraudUsers.map((u) => [
      u?.id || '—',
      u?.name || '—',
      u?.email || '—',
      u?.phone ? formatPhone(u.phone) : '—',
      u?.username || '—',
      u?.fraudReason || 'Suspicious activity',
      u?.fraudFlaggedBy || 'SYSTEM',
      u?.fraudFlaggedAt ? dayjs(u.fraudFlaggedAt).format('YYYY-MM-DD HH:mm') : '—',
      u?.isBlocked ? 'Yes' : 'No',
      u?.isActive ? 'Yes' : 'No',
    ]);

    dataToExport.unshift(header);
    const worksheet = XLSX.utils.aoa_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Blacklisted_Users');
    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });
    saveAs(
      new Blob([excelBuffer], { type: 'application/octet-stream' }),
      `Blacklisted_Users_Page_${page + 1}.xlsx`
    );
  };

  const StatMini = ({ label, value, color, icon: Icon }) => (
    <Box
      sx={{
        p: 2.25,
        borderRadius: '18px',
        bgcolor: '#fff',
        border: `1px solid ${alpha(color, 0.18)}`,
        boxShadow: `0 4px 20px ${alpha(color, 0.08)}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <Box>
        <Typography
          variant="caption"
          sx={{
            color: '#64748b',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            fontSize: '0.7rem',
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: '1.65rem',
            color: '#0f172a',
            lineHeight: 1.2,
            mt: 0.25,
          }}
        >
          {value}
        </Typography>
      </Box>
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: '12px',
          bgcolor: alpha(color, 0.1),
          color: color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon sx={{ fontSize: 24 }} />
      </Box>
    </Box>
  );

  return (
    <Box>
      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <motion.div custom={0} initial="hidden" animate="visible" variants={fadeUp}>
            <StatMini
              label="Total Blacklisted Users"
              value={stats.totalFraud.toLocaleString()}
              color="#dc2626"
              icon={GppBadIcon}
            />
          </motion.div>
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <motion.div custom={1} initial="hidden" animate="visible" variants={fadeUp}>
            <StatMini
              label="System Auto-Flagged (≥3 Failures)"
              value={stats.systemAutoCount.toLocaleString()}
              color="#7c3aed"
              icon={SmartToyOutlinedIcon}
            />
          </motion.div>
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <motion.div custom={2} initial="hidden" animate="visible" variants={fadeUp}>
            <StatMini
              label="Admin Blacklisted"
              value={stats.adminFlaggedCount.toLocaleString()}
              color="#d97706"
              icon={AdminPanelSettingsOutlinedIcon}
            />
          </motion.div>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <motion.div custom={3} initial="hidden" animate="visible" variants={fadeUp}>
            <StatMini
              label="Security Lockouts"
              value={stats.blockedCount.toLocaleString()}
              color="#0f172a"
              icon={LockPersonIcon}
            />
          </motion.div>
        </Grid>
      </Grid>

      {/* Filter Card */}
      <motion.div custom={4} initial="hidden" animate="visible" variants={fadeUp}>
        <PanelCard sx={{ mb: 3, p: { xs: 2, md: 2.5 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
            spacing={2}
            mb={2}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <FilterListOutlinedIcon sx={{ color: '#dc2626' }} />
              <Typography sx={{ fontWeight: 700, color: '#0f172a' }}>
                Search Blacklisted Accounts & Lockouts
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1.5} alignItems="center">
              <Tooltip title="Refresh blacklist">
                <IconButton
                  onClick={fetchFraudUsers}
                  disabled={loading}
                  sx={{
                    bgcolor: alpha('#dc2626', 0.08),
                    color: '#dc2626',
                    '&:hover': { bgcolor: alpha('#dc2626', 0.15) },
                  }}
                >
                  <RefreshOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <CustomButton
                variant="outlined"
                onClick={exportToExcel}
                disabled={loading || fraudUsers.length === 0}
                startIcon={<FileDownloadOutlinedIcon />}
                sx={{
                  borderRadius: '12px',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderColor: alpha('#dc2626', 0.25),
                  color: '#dc2626',
                  '&:hover': {
                    borderColor: '#dc2626',
                    bgcolor: alpha('#dc2626', 0.04),
                  },
                }}
              >
                Export page
              </CustomButton>
            </Stack>
          </Stack>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={9}>
              <SearchBar
                onSearch={handleSearch}
                searchTerm={searchTerm}
                height="2.75rem"
                placeholder="Search by name, email, phone, username, or reason..."
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <CustomButton
                variant="outlined"
                onClick={handleClearFilters}
                fullWidth
                sx={{
                  height: '2.75rem',
                  borderRadius: '12px',
                  textTransform: 'none',
                  fontWeight: 600,
                  borderColor: alpha('#dc2626', 0.25),
                  color: '#dc2626',
                  '&:hover': {
                    borderColor: '#dc2626',
                    bgcolor: alpha('#dc2626', 0.04),
                  },
                }}
              >
                Clear Search
              </CustomButton>
            </Grid>
          </Grid>
        </PanelCard>
      </motion.div>

      {/* Main Table */}
      <motion.div custom={5} initial="hidden" animate="visible" variants={fadeUp}>
        {loading ? (
          <PanelCard>
            <Stack alignItems="center" justifyContent="center" sx={{ py: 12 }}>
              <CircularProgress sx={{ color: '#dc2626' }} />
              <Typography sx={{ mt: 2, color: '#64748b', fontWeight: 500 }}>
                Loading blacklisted accounts…
              </Typography>
            </Stack>
          </PanelCard>
        ) : fraudUsers.length === 0 ? (
          <PanelCard>
            <Stack alignItems="center" justifyContent="center" sx={{ py: 10, px: 3 }}>
              <SecurityIcon sx={{ fontSize: 60, color: '#cbd5e1', mb: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
                No blacklisted accounts found
              </Typography>
              <Typography sx={{ color: '#64748b', mt: 1, textAlign: 'center', maxWidth: 460 }}>
                No users are currently blacklisted or flagged matching your search criteria.
              </Typography>
            </Stack>
          </PanelCard>
        ) : (
          <TableContainer component={PanelCard} sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: 1050 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: alpha('#dc2626', 0.04) }}>
                  <TableCell
                    sx={{
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      color: '#dc2626',
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      py: 2,
                    }}
                  >
                    User
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      color: '#dc2626',
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      py: 2,
                    }}
                  >
                    Contact
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      color: '#dc2626',
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      py: 2,
                    }}
                  >
                    Blacklist Reason
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      color: '#dc2626',
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      py: 2,
                    }}
                  >
                    Detection Source
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      color: '#dc2626',
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      py: 2,
                    }}
                  >
                    Flagged At
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      color: '#dc2626',
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      py: 2,
                    }}
                  >
                    Security Status
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      color: '#dc2626',
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      py: 2,
                      pr: 3,
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fraudUsers.map((user) => {
                  const isSystem = String(user.fraudFlaggedBy || '').toUpperCase() === 'SYSTEM';

                  return (
                    <TableRow
                      key={user.id || user.email || Math.random()}
                      sx={{
                        transition: 'background 0.2s',
                        '&:hover': { bgcolor: alpha('#dc2626', 0.02) },
                        '& td': {
                          borderBottom: `1px solid ${alpha('#dc2626', 0.06)}`,
                          py: 2,
                        },
                      }}
                    >
                      {/* User Identity */}
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                          <Avatar
                            src={user.pictureUrl || user.profile}
                            sx={{
                              width: 42,
                              height: 42,
                              bgcolor: 'linear-gradient(135deg, #dc2626, #991b1b)',
                              fontWeight: 700,
                              fontSize: '0.95rem',
                              border: '2px solid #fff',
                              boxShadow: '0 4px 12px rgba(220, 38, 38, 0.2)',
                            }}
                          >
                            {(user.name?.[0] || user.username?.[0] || 'U').toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography sx={{ fontWeight: 700, color: '#0f172a', fontSize: '0.92rem' }}>
                              {user.name || user.username || '—'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                              @{user.username || 'user'} • ID:{' '}
                              <span style={{ fontFamily: 'monospace' }}>{user.id}</span>
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>

                      {/* Contact */}
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Stack direction="row" alignItems="center" spacing={0.75}>
                            <PhoneOutlinedIcon sx={{ fontSize: 14, color: '#dc2626' }} />
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
                              {formatPhone(user.phone)}
                            </Typography>
                          </Stack>
                          <Stack direction="row" alignItems="center" spacing={0.75}>
                            <EmailOutlinedIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
                            <Typography variant="caption" sx={{ color: '#64748b' }}>
                              {user.email || '—'}
                            </Typography>
                          </Stack>
                        </Stack>
                      </TableCell>

                      {/* Fraud Reason */}
                      <TableCell sx={{ maxWidth: 260 }}>
                        <Box
                          sx={{
                            p: 1,
                            borderRadius: '10px',
                            bgcolor: alpha('#dc2626', 0.06),
                            border: `1px solid ${alpha('#dc2626', 0.15)}`,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: '#991b1b',
                              fontSize: '0.8rem',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {user.fraudReason || 'Suspicious activity detected'}
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* Detection Source */}
                      <TableCell>
                        <Chip
                          icon={
                            isSystem ? (
                              <SmartToyOutlinedIcon style={{ fontSize: 14, color: '#7c3aed' }} />
                            ) : (
                              <AdminPanelSettingsOutlinedIcon style={{ fontSize: 14, color: '#11409B' }} />
                            )
                          }
                          label={user.fraudFlaggedBy || 'SYSTEM'}
                          size="small"
                          sx={{
                            fontWeight: 700,
                            fontSize: '0.72rem',
                            bgcolor: isSystem ? alpha('#7c3aed', 0.1) : alpha('#11409B', 0.1),
                            color: isSystem ? '#7c3aed' : '#11409B',
                            border: `1px solid ${isSystem ? alpha('#7c3aed', 0.25) : alpha('#11409B', 0.25)}`,
                          }}
                        />
                      </TableCell>

                      {/* Flagged At */}
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#475569', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                          {user.fraudFlaggedAt ? dayjs(user.fraudFlaggedAt).format('DD MMM YYYY') : '—'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>
                          {user.fraudFlaggedAt ? dayjs(user.fraudFlaggedAt).format('HH:mm:ss') : ''}
                        </Typography>
                      </TableCell>

                      {/* Security Status */}
                      <TableCell>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <Chip
                            label="BLACKLISTED"
                            size="small"
                            sx={{
                              fontWeight: 800,
                              fontSize: '0.68rem',
                              bgcolor: alpha('#dc2626', 0.15),
                              color: '#dc2626',
                              border: `1px solid ${alpha('#dc2626', 0.3)}`,
                            }}
                          />
                          <Chip
                            label={user.isBlocked !== false ? 'Blocked' : 'Active'}
                            size="small"
                            sx={{
                              fontWeight: 700,
                              fontSize: '0.68rem',
                              bgcolor: alpha('#475569', 0.1),
                              color: '#334155',
                            }}
                          />
                        </Stack>
                      </TableCell>

                      {/* Actions */}
                      <TableCell align="right" sx={{ pr: 3 }}>
                        <Tooltip title="Remove user from blacklist & restore account">
                          <span>
                            <CustomButton
                              variant="outlined"
                              size="small"
                              onClick={() => handleOpenActionModal(user)}
                              startIcon={<LockOpenIcon fontSize="small" />}
                              sx={{
                                borderRadius: '10px',
                                textTransform: 'none',
                                fontWeight: 700,
                                fontSize: '0.78rem',
                                py: 0.5,
                                px: 1.5,
                                borderColor: alpha('#16a34a', 0.35),
                                color: '#16a34a',
                                '&:hover': {
                                  bgcolor: alpha('#16a34a', 0.08),
                                  borderColor: '#16a34a',
                                },
                              }}
                            >
                              Restore User
                            </CustomButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </motion.div>

      {/* Pagination Bar */}
      {!loading && fraudUsers.length > 0 && (
        <motion.div custom={6} initial="hidden" animate="visible" variants={fadeUp}>
          <PanelCard sx={{ mt: 3, p: 2 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems="center"
              justifyContent="space-between"
              spacing={2}
            >
              <Typography sx={{ color: '#64748b', fontWeight: 500, fontSize: '0.9rem' }}>
                Showing {page * size + 1}–
                {Math.min((page + 1) * size, totalElements)} of {totalElements} blacklisted users
              </Typography>

              <Stack direction="row" alignItems="center" spacing={2}>
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                  Rows per page:
                </Typography>
                <Box
                  component="select"
                  value={size}
                  onChange={(e) => {
                    setSize(Number(e.target.value));
                    setPage(0);
                  }}
                  sx={{
                    border: `1px solid ${alpha('#dc2626', 0.2)}`,
                    borderRadius: '10px',
                    px: 1.5,
                    py: 0.75,
                    fontSize: '0.875rem',
                    color: '#0f172a',
                    bgcolor: '#fff',
                    outline: 'none',
                    cursor: 'pointer',
                  }}
                >
                  {[10, 20, 50, 100].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </Box>

                <Stack direction="row" alignItems="center" spacing={1}>
                  <IconButton
                    onClick={() => setPage((p) => Math.max(p - 1, 0))}
                    disabled={page === 0}
                    sx={{
                      border: '1px solid',
                      borderColor: alpha('#dc2626', 0.15),
                      borderRadius: '12px',
                      bgcolor: '#fff',
                      '&:hover': { bgcolor: alpha('#dc2626', 0.06) },
                    }}
                  >
                    <KeyboardArrowLeftIcon />
                  </IconButton>
                  <Typography sx={{ fontWeight: 600, minWidth: 54, textAlign: 'center' }}>
                    {page + 1} / {totalPages || 1}
                  </Typography>
                  <IconButton
                    onClick={() => setPage((p) => Math.min(p + 1, totalPages - 1))}
                    disabled={page >= totalPages - 1}
                    sx={{
                      border: '1px solid',
                      borderColor: alpha('#dc2626', 0.15),
                      borderRadius: '12px',
                      bgcolor: '#fff',
                      '&:hover': { bgcolor: alpha('#dc2626', 0.06) },
                    }}
                  >
                    <KeyboardArrowRightIcon />
                  </IconButton>
                </Stack>
              </Stack>
            </Stack>
          </PanelCard>
        </motion.div>
      )}

      {/* Fraud Modal */}
      <FraudModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
        onSuccess={() => {
          fetchFraudUsers();
        }}
      />
    </Box>
  );
};

export default FraudUsersTab;
